/**
 * WelcomeCompletePage - Step 5 of App Orientation Flow
 * 
 * Congratulates user on completion, provides summary of accomplishments,
 * offers quick reference resources, and transitions to main application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../api/enhancedApiService';
import { 
  CheckCircleIcon,
  TrophyIcon,
  BookOpenIcon,
  UserGroupIcon,
  ChatBubbleLeftEllipsisIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClockIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import Logo from '../../components/common/Logo';

const WelcomeCompletePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [welcomeProgress, setWelcomeProgress] = useState(null);

  useEffect(() => {
    // Load progress data from backend
    const loadProgress = async () => {
      try {
        const status = await userAPI.getOnboardingStatus();
        setWelcomeProgress(status?.onboarding_progress || null);
      } catch (error) {
        console.error('[WelcomeComplete] Error loading progress:', error);
        
        // Fallback to localStorage for compatibility
        const progressData = localStorage.getItem('welcomeFlowProgress');
        if (progressData) {
          setWelcomeProgress(JSON.parse(progressData));
        }
      }
    };

    loadProgress();
  }, []);

  const handleFinish = async () => {
    try {
      // Calculate completion time if we have start time
      let durationMinutes = null;
      if (welcomeProgress?.started_at) {
        const startTime = new Date(welcomeProgress.started_at);
        const endTime = new Date();
        durationMinutes = Math.round((endTime - startTime) / (1000 * 60));
      }

      // Mark welcome flow as completed in backend
      await userAPI.completeOnboarding('welcome_flow', durationMinutes);
      
      console.log('[WelcomeComplete] Completed onboarding tracking');
      
      // Wait a moment for database update to propagate before navigation
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('[WelcomeComplete] Error completing onboarding:', error);
      
      // Fallback to localStorage for compatibility
      localStorage.setItem(`welcome_completed_${user?.id}`, 'true');
      localStorage.setItem('welcome_completion_date', new Date().toISOString());
    }
    
    // Clear progress data (cleanup)
    localStorage.removeItem('welcomeFlowProgress');
    
    // Navigate to dashboard
    navigate('/dashboard');
  };

  const accomplishments = [
    {
      icon: CheckCircleIcon,
      title: 'Data Landscape Mastery',
      description: 'You explored your connected data sources and understand the health scoring system.',
      color: 'text-green-600'
    },
    {
      icon: SparklesIcon,
      title: 'Feature Familiarity',
      description: 'You learned the key features and know how to navigate the main sections.',
      color: 'text-purple-600'
    },
    {
      icon: AcademicCapIcon,
      title: 'Hands-On Experience',
      description: 'You completed practical tasks with real data to build confidence.',
      color: 'text-blue-600'
    }
  ];

  const quickActions = [
    {
      title: 'Start Monitoring',
      description: 'Jump into the dashboard to see your data quality overview',
      action: 'Go to Dashboard',
      link: '/dashboard',
      color: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      title: 'Explore Data',
      description: 'Dive deeper into your tables and data structure',
      action: 'Open Data Explorer',
      link: '/explorer',
      color: 'bg-green-600 hover:bg-green-700'
    },
    {
      title: 'Check Quality',
      description: 'Review validation results and data quality status',
      action: 'View Validations',
      link: '/validations',
      color: 'bg-purple-600 hover:bg-purple-700'
    }
  ];

  const resources = [
    {
      icon: BookOpenIcon,
      title: 'Documentation',
      description: 'Detailed guides and best practices',
      link: '#', // TODO: Add actual documentation link
      external: true
    },
    {
      icon: UserGroupIcon,
      title: 'Team Settings',
      description: 'Invite team members and manage permissions',
      link: '/admin/users',
      external: false
    },
    {
      icon: ChatBubbleLeftEllipsisIcon,
      title: 'Support',
      description: 'Get help when you need it',
      link: '#', // TODO: Add support link
      external: true
    }
  ];

  const getCompletionStats = () => {
    if (!welcomeProgress) return { timeSpent: '~10 minutes', tasksCompleted: 'N/A' };
    
    const startTime = new Date(welcomeProgress.startedAt);
    const endTime = new Date();
    const timeSpent = Math.round((endTime - startTime) / (1000 * 60)); // minutes
    
    const tasksCompleted = welcomeProgress.tasksCompleted?.completedTaskIds?.length || 0;
    const totalTasks = welcomeProgress.tasksCompleted?.totalTasks || 4;
    
    return {
      timeSpent: `${timeSpent} minute${timeSpent !== 1 ? 's' : ''}`,
      tasksCompleted: `${tasksCompleted}/${totalTasks}`,
      completionRate: welcomeProgress.tasksCompleted?.completionRate || 0
    };
  };

  const stats = getCompletionStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Logo size="medium" withText={true} />
            <div className="text-sm text-gray-500">Welcome Tour • Complete!</div>
          </div>
        </div>
      </div>

      {/* Progress Bar - Complete */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full w-full"></div>
            </div>
            <span className="ml-3 text-sm text-green-600 font-medium">Complete!</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Celebration Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <TrophyIcon className="w-10 h-10 text-green-600" />
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Congratulations!
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
            You've successfully completed the Sparvi orientation. You're now ready 
            to monitor, explore, and maintain your data quality like a pro!
          </p>

          {/* Quick Stats */}
          <div className="inline-flex items-center space-x-6 bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center text-sm">
              <ClockIcon className="w-4 h-4 text-gray-500 mr-2" />
              <span className="text-gray-600">Time spent:</span>
              <span className="ml-1 font-medium text-gray-900">{stats.timeSpent}</span>
            </div>
            <div className="flex items-center text-sm">
              <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
              <span className="text-gray-600">Tasks completed:</span>
              <span className="ml-1 font-medium text-gray-900">{stats.tasksCompleted}</span>
            </div>
          </div>
        </div>

        {/* Accomplishments */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            What You've Accomplished
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {accomplishments.map((accomplishment, index) => {
              const IconComponent = accomplishment.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-4">
                    <IconComponent className={`w-6 h-6 ${accomplishment.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {accomplishment.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {accomplishment.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Ready to Get Started?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.link}
                className={`block p-6 rounded-lg text-white transition-colors ${action.color}`}
              >
                <h3 className="font-semibold mb-2">{action.title}</h3>
                <p className="text-sm opacity-90 mb-4">{action.description}</p>
                <div className="flex items-center text-sm font-medium">
                  {action.action}
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Additional Resources */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Helpful Resources</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {resources.map((resource, index) => {
              const IconComponent = resource.icon;
              const LinkComponent = resource.external ? 'a' : Link;
              const linkProps = resource.external 
                ? { href: resource.link, target: '_blank', rel: 'noopener noreferrer' }
                : { to: resource.link };
              
              return (
                <LinkComponent
                  key={index}
                  {...linkProps}
                  className="flex items-start p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <IconComponent className="w-5 h-5 text-gray-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">{resource.title}</h3>
                    <p className="text-sm text-gray-600">{resource.description}</p>
                  </div>
                </LinkComponent>
              );
            })}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-blue-900 mb-4">Your First Week with Sparvi</h2>
          <div className="space-y-3 text-blue-800">
            <div className="flex items-start">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <span className="text-sm">Check the dashboard daily to monitor data quality trends</span>
            </div>
            <div className="flex items-start">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <span className="text-sm">Set up email alerts for critical validation failures</span>
            </div>
            <div className="flex items-start">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <span className="text-sm">Invite your team members and show them the key features</span>
            </div>
            <div className="flex items-start">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <span className="text-sm">Create issues for any data quality problems you discover</span>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center">
          <button
            onClick={handleFinish}
            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Enter Your Dashboard
            <ArrowRightIcon className="ml-3 h-6 w-6" />
          </button>
          
          <p className="mt-4 text-sm text-gray-500">
            You can always revisit this tour from the help menu
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeCompletePage;